import React, { useCallback, useMemo } from 'react'
import { sortBy } from 'lib/array/arrayUtils'
import useQueryParams from 'hooks/useQueryParams'
import { OfferPageActions, OfferPageMultiRoomAutoselectionReason, OfferPageMultiRoomMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { GOOGLE_ADS_UTM_CONTENT } from 'constants/bedbank'
import Sheet from 'components/utils/Sheet'
import OfferPageRoomModeToggleSection from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPageRoomModeToggleSection'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import OfferPageTogglesSection from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPageTogglesSection'
import BedbankMultiSameRoomRoom from './BedbankMultiSameRoomRoom'
import BedbankMultiIndividualRoomRoom from './BedbankMultiIndividualRoomRoom'
import { isBedbankMultiRoomEnabled } from 'selectors/offerPage/bedbankOfferPageSelectors'
import uuidV4 from 'lib/string/uuidV4Utils'
import BedbankMultiRoomBuySection from './BedbankMultiRoomBuySection'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import BedbankMultiRoomNoDatesCTA from './BedbankMultiRoomNoDatesCTA'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { selectLoggedIn } from 'selectors/accountSelectors'
import BedbankMultiRoomLoginCTA from './BedbankMultiRoomLoginCTA'

interface Props {
  checkInDate?: string;
  checkOutDate?: string;
  offer: App.BedbankOffer;
  onViewPackageOptions?: () => void;
  packageViews: Array<App.BedbankOfferPackageView>;
  fetchingRates?: boolean;
  rooms: Array<OfferPageRoom>;
  multiRoomEnabled?: boolean;
  roomMode?: OfferPageMultiRoomMode;
  autoselectionReason?: OfferPageMultiRoomAutoselectionReason;
  /** This one will be shown at the top of the list */
  topPackageId?: string;
  flightOrigin?: App.Airport;
  isLoggedIn: boolean;
}

function BedbankRoomOptionsMobile(props: Props) {
  const {
    checkInDate,
    checkOutDate,
    offer,
    packageViews,
    fetchingRates,
    rooms,
    multiRoomEnabled,
    roomMode,
    autoselectionReason,
    topPackageId: topPackageIdProp,
    flightOrigin,
    isLoggedIn,
  } = props

  const query = useQueryParams()
  const validCheckinCheckoutDates = !!(checkInDate && checkOutDate)
  const utmContent = query.get('utm_content')
  const utmRoomTypeId = utmContent === GOOGLE_ADS_UTM_CONTENT ? query.get('roomTypeId') : null
  const topPackageId = utmRoomTypeId || topPackageIdProp
  const dispatch = useOfferPageDispatch()

  const hasPromotions = offer.promotions.length > 0

  const sortedPackages = useMemo(() => {
    const sortedPackageViews = sortBy(packageViews, pkg => {
      if (pkg.cheapestRate) {
        return pkg.cheapestRate.totals.inclusive + (pkg.cheapestRate.totals.propertyFees ?? 0)
      } else if (hasPromotions) {
        return Number.MAX_SAFE_INTEGER
      }

      if (validCheckinCheckoutDates) {
        if (!pkg.capacityValid) {
          return Number.MAX_SAFE_INTEGER - 1
        }

        return Number.MAX_SAFE_INTEGER
      }

      if (pkg.capacityValid) {
        return Number.MIN_SAFE_INTEGER
      }

      return Number.MAX_SAFE_INTEGER
    }, 'asc')

    const highlightedPackage = sortedPackageViews.find(p => p.package.roomId === topPackageId)
    if (highlightedPackage) {
      return [
        highlightedPackage,
        ...sortedPackageViews.filter(p => p.package.roomId !== topPackageId),
      ]
    }
    else {
      return sortedPackageViews
    }
  }, [packageViews, hasPromotions, validCheckinCheckoutDates, topPackageId])

  const displayablePackages = useMemo(() => {
    const showAll = sortedPackages.filter(p => p.available).length === 0
    return sortedPackages.filter(packageView => packageView.show && (packageView.available || showAll))
  }, [sortedPackages])

  const roomListId = useMemo(() => uuidV4(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayablePackages]) // regenerate id if packages change

  const onModeChange = useCallback((mode: OfferPageMultiRoomMode) => {
    dispatch({ type: OfferPageActions.toggleMultiRoomMode, multiRoomMode: mode })
  }, [dispatch])

  const canUseMultiRoomUI = rooms.length > 1 && multiRoomEnabled

  const isMultiRoomUI = canUseMultiRoomUI && roomMode === 'individual'

  const totalFlightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: flightOrigin?.code,
  })

  const isHorizontalSliderEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.croHorizontalScrollFlashPackages)

  let bottomCTA: React.ReactNode = null
  if (!isLoggedIn) {
    bottomCTA = <BedbankMultiRoomLoginCTA />
  } else if (!validCheckinCheckoutDates) {
    bottomCTA = <BedbankMultiRoomNoDatesCTA />
  } else {
    bottomCTA = <BedbankMultiRoomBuySection />
  }

  return <Sheet background="neutral-six" padding="8 0">
    <VerticalSpacer gap={8}>
      {canUseMultiRoomUI && <OfferPageRoomModeToggleSection
        mode={roomMode}
        rooms={rooms}
        saleUnit={offer.saleUnit}
        onChange={onModeChange}
        autoselectionReason={autoselectionReason}
      />}

      <OfferPageTogglesSection totalFlightPrice={totalFlightPrice} />

      {isMultiRoomUI && <>
        {rooms.map((room, i) => {
          return <BedbankMultiIndividualRoomRoom
            key={room.id}
            room={room}
            packageNumber={i + 1}
          />
        })}
        {bottomCTA}
      </>}
      {!isMultiRoomUI && displayablePackages.map((packageView, i) => <BedbankMultiSameRoomRoom
        key={packageView.package.roomId}
        packageView={packageView}
        optionVariant={isHorizontalSliderEnabled ? 'carousel' : 'stacked'}
        fetchingRates={fetchingRates}
        roomListId={roomListId}
        roomPosition={i + 1}
        youWereInterested={utmContent === GOOGLE_ADS_UTM_CONTENT && utmRoomTypeId === packageView.package.roomId}
      />)}
    </VerticalSpacer>
  </Sheet>
}

type MappedProps = Pick<Props,
  | 'checkInDate'
  | 'checkOutDate'
  | 'rooms'
  | 'multiRoomEnabled'
  | 'roomMode'
  | 'autoselectionReason'
  | 'flightOrigin'
  | 'isLoggedIn'
> & { open: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => {
  return {
    checkInDate: state.checkInDate,
    checkOutDate: state.checkOutDate,
    open: !!state.activeFilter,
    rooms: state.rooms,
    multiRoomEnabled: isBedbankMultiRoomEnabled(state, appState),
    roomMode: state.multiRoomMode,
    autoselectionReason: state.multiRoomAutoselectionReason,
    flightOrigin: state.airport,
    isLoggedIn: selectLoggedIn(appState),
  }
}, BedbankRoomOptionsMobile)
